import React from "react";
import { Grid, TextField, MenuItem, InputAdornment, IconButton } from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { NumericFormat } from "react-number-format";
import { inputStyle } from "./styles";
import { deriveParamMeta, formatParamName } from "./strategyParamUtils";

const isParamEditableInEditRfq = (param = {}) => {
  const rawValue = param.isEditable;
  if (rawValue === undefined || rawValue === null) return true;
  if (typeof rawValue === "boolean") return rawValue;
  if (typeof rawValue === "number") return rawValue === 1;

  const normalized = String(rawValue).trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "y" || normalized === "yes";
};

const StrategyParamFields = ({
  params = [],
  values = {},
  errors = {},
  onChange,
  isFieldEditable,
  editRfqMode = false,
}) => {
  const handleSelectChange = (meta, paramName, raw) => {
    const cast = meta.castValue ? meta.castValue(raw) : raw;
    onChange(paramName, cast);
  };

  const handleNumberChange = (meta, paramName, floatValue) => {
    const cast = meta.castValue ? meta.castValue(floatValue) : floatValue;
    onChange(paramName, cast);
  };

  const renderField = (param) => {
    const meta = deriveParamMeta(param);
    const paramKey = param.paramName || param.name || param.id;
    const fieldEditable =
      isFieldEditable(paramKey) && (!editRfqMode || isParamEditableInEditRfq(param));
    let value = values[paramKey] ?? "";

    const error = errors?.[paramKey];

    if (meta.inputType === "select" && Array.isArray(meta.options)) {
      const normalizeOption = (opt) =>
        typeof opt === "object" && opt !== null && Object.prototype.hasOwnProperty.call(opt, "value")
          ? opt.value
          : opt;
      const normalizeLabel = (opt) =>
        typeof opt === "object" && opt !== null && Object.prototype.hasOwnProperty.call(opt, "label")
          ? opt.label
          : normalizeOption(opt);
      const selectRenderValue = (selected) => {
        if (selected === "" || selected === undefined || selected === null) {
          return "";
        }
        const match = meta.options.find((opt) => {
          const optionValue = normalizeOption(opt);
          return String(optionValue) === String(selected);
        });
        return match ? normalizeLabel(match) : selected;
      };

      return (
        <TextField
          select
          fullWidth
          variant="filled"
          name={paramKey}
          label={formatParamName(paramKey)}
          value={value}
          onChange={fieldEditable ? (e) => handleSelectChange(meta, paramKey, e.target.value) : undefined}
          disabled={!fieldEditable}
          error={!!error}
          sx={{
            ...inputStyle,
            "& .MuiInputBase-input, & .MuiSelect-select": {
              paddingRight: value && !editRfqMode && fieldEditable ? "80px" : "44px",
              textOverflow: "ellipsis",
              overflow: "hidden",
              whiteSpace: "nowrap",
            },
            "& .MuiSelect-icon": {
              right: value && !editRfqMode && fieldEditable ? "24px" : "0px",
              pointerEvents: "none",
            },
          }}
          SelectProps={{
            displayEmpty: true,
            renderValue: selectRenderValue,
          }}
          InputProps={{
            disableUnderline: true,
            endAdornment: value && !editRfqMode && fieldEditable ? (
              <InputAdornment position="end" sx={{ mr: 0,mb: 1 }}>
                <IconButton
                  aria-label="Clear selection"
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    handleSelectChange(meta, paramKey, "");
                  }}
                  edge="end"
                  sx={{ 
                    position: "absolute", 
                    right: 8, 
                    padding: 0,
                    zIndex: 1,
                    '&:hover': {
                      backgroundColor: 'transparent',
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : undefined,
          }}
        >
          {meta.options.map((opt) => {
            const optionValue = normalizeOption(opt);
            const optionLabel = normalizeLabel(opt);
            return (
              <MenuItem key={`${paramKey}-${optionValue}`} value={optionValue}>
                {optionLabel}
              </MenuItem>
            );
          })}
        </TextField>
      );
    }

    if (meta.inputType === "number") {
      return (
        <NumericFormat
          customInput={TextField}
          fullWidth
          variant="filled"
          name={paramKey}
          label={formatParamName(paramKey)}
          value={value}
          thousandSeparator=","
          allowNegative={meta.minExclusive === undefined && meta.minInclusive === undefined}
          decimalScale={param.dataType === "INT" ? 0 : 6}
          placeholder={meta.hint}
          onValueChange={({ floatValue, value: raw }) =>
            fieldEditable ? handleNumberChange(meta, paramKey, raw === "" ? "" : floatValue) : undefined
          }
          disabled={!fieldEditable}
          error={!!error}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        />
      );
    }

    return (
      <TextField
        fullWidth
        variant="filled"
        name={paramKey}
        label={formatParamName(paramKey)}
        value={value}
        onChange={fieldEditable ? (e) => onChange(paramKey, e.target.value) : undefined}
        disabled={!fieldEditable}
        error={!!error}
        sx={inputStyle}
        placeholder={meta.hint || undefined}
        InputProps={{ disableUnderline: true }}
      />
    );
  };

  if (!params.length) {
    return null;
  }

  return (
    <>
      {params.map((param) => {
        const paramKey = param.paramName || param.name || param.id;
        return (
          <Grid key={paramKey} item xs={3}>
            {renderField(param)}
          </Grid>
        );
      })}
    </>
  );
};

export default StrategyParamFields;
